import { Page, Text, View, StyleSheet, Image, Font } from "@react-pdf/renderer";
import dayjs from "dayjs";
import logo from "@/img/lansing-logo.jpeg";
import { OrderDataForPDF } from "@/app/interfaces/response/OrderPdfDataResponse";
import CommonHelper from "@/app/helpers/CommonHelper";
import { isSAPSource } from "@/app/utils/erpHelper";

Font.registerHyphenationCallback((w) => [w]);

export default function OrderPagePDF({ order }: { order: OrderDataForPDF }) {
  const AddressBlock = ({
    label,
    name,
    address1,
    address2,
    city,
    state,
    zip,
    country,
  }: {
    label: string;
    name?: string | null;
    address1?: string | null;
    address2?: string | null;
    city?: string | null;
    state?: string | null;
    zip?: string | null;
    country?: string | null;
  }) => (
    <View style={styles.addressRow}>
      <Text style={styles.addressLabel}>{label}:</Text>

      <View style={styles.addressLines}>
        {name ? <Text style={styles.addrBold}>{name}</Text> : null}
        {address1 ? <Text style={styles.addrBold}>{address1}</Text> : null}
        {address2 ? <Text style={styles.addrBold}>{address2}</Text> : null}

        {(!!city || !!state || !!zip) && (
          <Text style={styles.addrBold}>
            {(city || "").toString().trim()}
            {state ? ` , ${state}` : ""}
            {zip ? ` , ${zip}` : ""}
          </Text>
        )}

        {country && <Text style={styles.addrCountry}>{country}</Text>}
      </View>
    </View>
  );

  const LabelValue = ({ label, value }: { label: string; value?: string }) => (
    <Text>
      <Text>{label}: </Text>
      <Text style={styles.bold}>{value ?? "-"}</Text>
    </Text>
  );

  let isSAPOrder = isSAPSource(order.source ?? "");

  const RowTotal = ({ label, value }: { label: string; value?: string }) => (
    <View style={styles.totalRow}>
      <Text style={styles.totalsLabel}>{label}</Text>
      <Text style={styles.totalsValue}>{value}</Text>
    </View>
  );

  const createdOn = order?.createdOn
    ? dayjs(order.createdOn).format("dddd, MMMM D, YYYY")
    : "-";

  return (
    <Page size="A4" style={styles.page}>
      <View style={styles.headerFixed} fixed>
        <Image src={logo} style={styles.logo} />
      </View>
      <View style={{ gap: 2, marginTop: 10 }}>
        <Text style={styles.h1}>Order #{order?.customOrderNumber}</Text>
        {order.isBackordered && (
          <Text style={styles.backorderNote}>
            BACKORDERED ITEMS APPEAR ON THIS ORDER
          </Text>
        )}
        <LabelValue label="Order Type" value={order?.inforOrderType} />
        <LabelValue label="Order Status" value={order?.orderStatus} />
        <LabelValue label="Order Created Date" value={createdOn} />
        <LabelValue label="Order Invoice Date" value={order?.invoiceDate} />
        <LabelValue label="Invoice Number" value={order?.customOrderNumber} />
      </View>
      <View style={{ gap: 2, marginVertical: 10 }}>
        <LabelValue
          label="Order Total"
          value={CommonHelper.addDolarSign(order?.orderTotal)}
        />
        <LabelValue label="Job Name" value={order?.jobName} />
        <LabelValue label="Branch" value={order?.warehouse} />
        <LabelValue
          label="Delivery/Pickup"
          value={order?.pickUpInStore ? "Pickup" : "Delivery"}
        />
        {order.placedByUserEmail && (
          <LabelValue label="Placed By" value={order?.placedByUserEmail} />
        )}
        {order?.pickUpInStore ? (
          <AddressBlock
            label="Pickup point address"
            name={order.pickupAddress?.firstName}
            address1={order.pickupAddress?.address1}
            address2={order.pickupAddress?.address2 || undefined}
            city={order.pickupAddress?.city}
            state={order.pickupAddress?.stateProvinceName || undefined}
            zip={order.pickupAddress?.zipPostalCode || undefined}
            country={order.pickupAddress?.county}
          />
        ) : (
          <AddressBlock
            label="Delivery Address"
            name={order.shippingAddress?.firstName}
            address1={order.shippingAddress?.address1}
            address2={order.shippingAddress?.address2 || undefined}
            city={order.shippingAddress?.city}
            state={order.shippingAddress?.stateProvinceName || undefined}
            zip={order.shippingAddress?.zipPostalCode || undefined}
            country={order.shippingAddress?.county}
          />
        )}
      </View>
      <Text style={styles.sectionTitle}>Billing Adress:</Text>
      <View style={{ gap: 2 }}>
        <LabelValue label="Name" value={order?.billingAddress.firstName} />
        <LabelValue label="Phone" value={order?.billingAddress.phoneNumber} />
        <LabelValue label="Address" value={order?.billingAddress.address1} />
        <LabelValue label="Address2" value={order?.billingAddress.address2} />
        <LabelValue label="City" value={order?.billingAddress.city} />
        <LabelValue
          label="State"
          value={order?.billingAddress.stateProvinceName}
        />
        {order?.billingAddress.county && (
          <LabelValue label="County" value={order?.billingAddress.county} />
        )}
        <LabelValue label="Zip" value={order?.billingAddress.zipPostalCode} />
      </View>

      <Text style={styles.sectionTitle}>
        {order.orderStatus === "Quoted" ? "Quote" : "Order"} Details
      </Text>
      <View style={styles.tableWrapper} wrap>
        <View style={styles.tableHeader} fixed>
          <View style={[styles.tr, styles.thead]}>
            <Text wrap={false} style={[styles.th, styles.cLine]}>
              Line #
            </Text>
            <Text wrap={false} style={[styles.th, styles.cItem]}>
              Item #
            </Text>
            <Text wrap={false} style={[styles.th, styles.cDesc]}>
              Item Name/Description
            </Text>
            <Text wrap={false} style={[styles.th, styles.cColor]}>
              Color
            </Text>
            <Text wrap={false} style={[styles.th, styles.cMeas]}>
              Measure
            </Text>
            <Text wrap={false} style={[styles.th, styles.cQty]}>
              Qty Ord
            </Text>
            <Text wrap={false} style={[styles.th, styles.cAvail]}>
              Qty Available
            </Text>
            <Text wrap={false} style={[styles.th, styles.cUom]}>
              UOM
            </Text>
            <Text wrap={false} style={[styles.th, styles.cPrice, styles.right]}>
              Unit Price
            </Text>
            <Text
              wrap={false}
              style={[styles.th, styles.cDiscount, styles.right]}
            >
              Unit Discount
            </Text>
            <Text wrap={false} style={[styles.th, styles.cTotal, styles.right]}>
              Total
            </Text>
          </View>
        </View>

        <View style={styles.tableBody}>
          {(order?.items ?? []).map((item, i) => {
            const isLast = i === (order?.items?.length ?? 0) - 1;
            return (
              <View
                key={i}
                style={[
                  styles.trBody,
                  ...(i % 2 === 1 ? [styles.rowAlt] : []),
                  ...(isLast ? [styles.lastRow] : []),
                ]}
                wrap={false}
              >
                <Text style={[styles.td, styles.cLine]}>{i + 1}</Text>
                <Text style={[styles.td, styles.cItem]}>
                  {isSAPOrder ? item.sapProduct : item.sku}
                </Text>
                <View style={[styles.tdMulti, styles.cDesc]}>
                  <Text>{item.productName}</Text>
                  {item.isSpecial && (
                    <Text style={styles.redText}>Special Order</Text>
                  )}
                </View>{" "}
                <Text style={[styles.td, styles.cColor]}>
                  {item.color || ""}
                </Text>
                <Text style={[styles.td, styles.cMeas]}>
                  {item.measure || ""}
                </Text>
                <Text style={[styles.td, styles.cQty]}>
                  {Math.round(item.quantity)}
                </Text>
                <Text style={[styles.td, styles.cAvail]}>
                  {Math.round(item.quantityShipped)}
                </Text>
                <Text style={[styles.td, styles.cUom]}>{item.uom}</Text>
                <Text style={[styles.td, styles.cPrice, styles.right]}>
                  {CommonHelper.addDolarSign(item.unitPrice)}
                </Text>
                <Text style={[styles.td, styles.cDiscount, styles.right]}>
                  {CommonHelper.addDolarSign(item.discAmt)}
                </Text>
                <Text style={[styles.td, styles.cTotal, styles.right]}>
                  {CommonHelper.addDolarSign(item.subTotal)}
                </Text>
              </View>
            );
          })}
        </View>
      </View>
      <View style={styles.other_totals}>
        {order.orderTotalDiscountSKUs && (
          <RowTotal
            label={`Promo Savings [${order.orderTotalDiscountSKUs}]`}
            value={CommonHelper.addDolarSign(order.orderItemsDiscount)}
          />
        )}
        {order.credits !== 0 && (
          <RowTotal
            label="Credits / IMPACT Rewards"
            value={CommonHelper.addDolarSign(order.credits)}
          />
        )}
      </View>
      <View style={styles.totals}>
        <RowTotal
          label="Total"
          value={CommonHelper.addDolarSign(order.itemsTotal)}
        />
        {order.charges.map((charge) => (
          <RowTotal
            label={charge.key}
            value={CommonHelper.addDolarSign(charge.value)}
          />
        ))}
        <RowTotal
          label="Taxes"
          value={CommonHelper.addDolarSign(order.taxes)}
        />
        {order.orderDiscount !== 0 && (
          <RowTotal
            label="Order Discount"
            value={CommonHelper.addDolarSign(order.orderDiscount)}
          />
        )}
        <View style={styles.totalRow}>
          <Text style={styles.totalsLabel}>
            Invoice Total<Text style={styles.redText}>*</Text>
          </Text>
          <Text style={[styles.totalsValue, styles.bold]}>
            {CommonHelper.addDolarSign(order.orderTotal)}
          </Text>
        </View>
        <Text style={styles.taxesNote}>
          Applicable taxes or service charges are not reflected until your order
          is processed.
        </Text>
      </View>
    </Page>
  );
}

const cellBase = {
  paddingVertical: 5,
  paddingHorizontal: 5,
  borderRightWidth: 1,
  borderColor: "#000",
} as const;

const styles = StyleSheet.create({
  page: {
    fontFamily: "Helvetica",
    paddingTop: 60,
    paddingHorizontal: 18,
    paddingBottom: 18,
    fontSize: 8,
  },
  headerFixed: {
    position: "absolute",
    top: 18,
    left: 18,
    right: 18,
    paddingBottom: 8,
    flexDirection: "row",
    alignItems: "center",
  },
  logo: { width: 120, height: 32, objectFit: "contain", marginRight: 12 },
  h1: { fontSize: 8, fontWeight: 700 },
  bold: { fontWeight: 700 },

  addressRow: { flexDirection: "row" },
  addressLabel: { width: 70, textAlign: "left" },
  addressLines: { flexDirection: "column" },
  addrBold: { fontWeight: 700, marginBottom: 1.5 },
  addrCountry: { marginTop: 1 },

  sectionTitle: {
    fontSize: 8,
    marginTop: 10,
    marginBottom: 4,
    textAlign: "left",
  },

  tableWrapper: {
    width: "100%",
    marginTop: 4,
    borderColor: "#000",
  },

  tableHeader: {
    borderColor: "#000",
  },

  backorderNote: {
    fontStyle: "italic",
    fontSize: 9,
    color: "#000000",
    marginTop: 2,
  },

  thead: {
    backgroundColor: "#f3f3f3",
    borderColor: "#000",
  },

  tableBody: {
    borderColor: "#000",
  },

  tr: {
    flexDirection: "row",
    borderColor: "#000",
    alignItems: "stretch",
  },

  trBody: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderColor: "#000",
    alignItems: "stretch",
  },

  th: {
    ...cellBase,
    fontWeight: 700,
    fontSize: 8,
    textAlign: "center",
    borderTopWidth: 1,
    borderBottomWidth: 1,
  },
  td: {
    ...cellBase,
    fontSize: 8,
    textAlign: "center",
  },
  tdMulti: {
    ...cellBase,
    fontSize: 8,
    textAlign: "left",
    lineHeight: 1.15,
  },

  right: { textAlign: "right" },
  rowAlt: { backgroundColor: "#fafafa" },
  lastRow: { borderBottomWidth: 1 },

  cLine: { width: "7%", borderLeftWidth: 1 },
  cItem: { width: "9%" },
  cDesc: { width: "20%" },
  cColor: { width: "14%" },
  cMeas: { width: "8%" },
  cQty: { width: "5%" },
  cAvail: { width: "8%" },
  cUom: { width: "5%" },
  cPrice: { width: "8%" },
  cDiscount: { width: "9%" },
  cTotal: { width: "9%" },
  redText: {
    color: "red",
  },
  totals: {
    marginTop: 8,
    alignItems: "flex-end",
    justifyContent: "space-between",
    height: 50,
  },
  other_totals: {
    marginTop: 8,
    alignItems: "flex-end",
    justifyContent: "space-between",
    height: 30,
  },
  totalRow: { flexDirection: "row", gap: 2 },
  totalsLabel: { width: 200, textAlign: "right" },
  totalsValue: { width: 80, textAlign: "right" },
  taxesNote: {
    marginTop: 4,
    color: "red",
    fontSize: 7,
    textAlign: "right",
    width: 300,
    fontStyle: "italic",
  },
});
